/* cc-recently-viewed — records PDP views in localStorage and renders
   the "You recently viewed" carousel (Phase 5, 2026-07-09).
   Empire's renderer lived in the compiled bundle; behavior rebuilt from
   its section data contract (record handle, heading + clear button,
   "viewed X ago" per card). Cards arrive SERVER-rendered: each handle is
   fetched as /products/<handle>?section_id=cc-card-single (Section
   Rendering API, same recipe as cc-card-batch for the category rail),
   so stars, price, stock and the Locksmith gate are live from the
   product every time the row renders (2026-10-09 — until then the card
   came from the client twin with a rating stashed at view time, and a
   review deleted in Judge.me lingered in every shopper's history until
   they revisited that PDP). Carousel is Dawn's slider-component with
   the same markup shape as related-products. No observers, no polling.
   Storage: cc:recently-viewed = [{h: handle, t: epoch-ms}], newest
   first, capped at 12. Deleted/unpublished products self-heal (404s
   are pruned from storage). Section stays display:none until it has
   at least one card. */
(() => {
  if (customElements.get('cc-recently-viewed')) return;

  const KEY = 'cc:recently-viewed';
  const STORE_CAP = 12;

  const read = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(v) ? v.filter((e) => e && typeof e.h === 'string') : [];
    } catch {
      return [];
    }
  };
  /* Only {h, t} is persisted — entries written before 2026-10-09 also
     carried a stashed rating ({r, c}); it is never read and drops off on
     the next write. */
  const write = (entries) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(entries.slice(0, STORE_CAP).map((e) => ({ h: e.h, t: e.t }))));
    } catch {
      /* storage unavailable (private mode) — feature degrades silently */
    }
  };

  /* "Viewed X ago" — Empire's locale strings covered seconds/minutes/
     hours; days added for older entries (rebuild deviation). */
  const ago = (t) => {
    const s = Math.max(1, Math.round((Date.now() - t) / 1000));
    if (s < 60) return s === 1 ? '1 second ago' : s + ' seconds ago';
    const m = Math.round(s / 60);
    if (m < 60) return m === 1 ? '1 minute ago' : m + ' minutes ago';
    const h = Math.round(m / 60);
    if (h < 24) return h === 1 ? '1 hour ago' : h + ' hours ago';
    const d = Math.round(h / 24);
    return d === 1 ? '1 day ago' : d + ' days ago';
  };

  /* One server-rendered THE card per handle. Resolves to the card's
     outerHTML. The Section Rendering API answers a deleted/unpublished
     product with 200 + an EMPTY section (product is nil), and an
     unknown section_id with 404 — so an empty 200 means the product is
     gone (rejects with dead=true → pruned from storage), while 404 or
     any other failure (network, 5xx, section not yet on this theme)
     just skips the card this time and never touches the history. */
  const fetchCard = async (handle) => {
    const res = await fetch('/products/' + encodeURIComponent(handle) + '?section_id=cc-card-single');
    if (!res.ok) throw new Error(handle);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const card = doc.querySelector('.cc-cprod');
    if (!card) {
      const err = new Error(handle);
      err.dead = true;
      throw err;
    }
    return card.outerHTML;
  };

  class CCRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ccInit) return;
      this.dataset.ccInit = '1';

      const recordHandle = this.dataset.recordHandle;
      let entries = read();

      if (recordHandle) {
        entries = entries.filter((e) => e.h !== recordHandle);
        entries.unshift({ h: recordHandle, t: Date.now() });
        write(entries);
      }

      this.querySelector('[data-cc-rv-clear]').addEventListener('click', () => {
        write([]);
        this.classList.remove('is-active');
      });

      const limit = parseInt(this.dataset.limit, 10) || 10;
      const show = entries.filter((e) => e.h !== recordHandle).slice(0, limit);
      if (!show.length) return;

      this.render(show, entries);
    }

    async render(show, allEntries) {
      const results = await Promise.allSettled(show.map((e) => fetchCard(e.h)));

      const cards = [];
      const dead = new Set();
      results.forEach((res, i) => {
        if (res.status === 'fulfilled') cards.push({ html: res.value, t: show[i].t });
        else if (res.reason && res.reason.dead) dead.add(show[i].h);
      });
      if (dead.size) write(allEntries.filter((e) => !dead.has(e.h)));
      if (!cards.length) return;

      const sid = this.dataset.sectionId || 'ccrv';
      const slides = cards
        .map(
          (c, i) => `<li id="Slide-ccrv-${sid}-${i + 1}" class="grid__item slider__slide">
            ${c.html}
            <p class="cc-recently-viewed__ago">Viewed ${ago(c.t)}</p>
          </li>`
        )
        .join('');

      /* Dawn's SliderComponent initializes in its CONSTRUCTOR — the
         element must upgrade with its children already present, so the
         whole carousel is injected as one parsed HTML string (same as
         Dawn's own ProductRecommendations innerHTML swap). Unhide first
         so the constructor measures real widths. */
      const arrowsTpl = this.querySelector('template[data-cc-rv-arrows]');
      const arrowsHTML = arrowsTpl ? arrowsTpl.innerHTML : '';
      this.classList.add('is-active');
      this.querySelector('[data-cc-rv-track]').innerHTML =
        `<slider-component class="slider-mobile-gutter">
          <ul id="Slider-ccrv-${sid}" class="grid product-grid slider slider--everywhere" role="list">${slides}</ul>
          <cc-carousel-bar aria-hidden="true"></cc-carousel-bar>
          ${arrowsHTML}
        </slider-component>`;
    }
  }

  customElements.define('cc-recently-viewed', CCRecentlyViewed);
})();
